import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import universalstorageHandler from "../persistence/UniversalstorageHandler";
import ServerDataBaseHandler from "../persistence/SeverDataBaseHandler";
import { ApplicationMode } from "../models/SettingsEnums";
import { loadLanguage, itx } from "../translationItx/itx";
import { setSession } from "./account/auth-helper";
import { loadProject } from "./LoadProject";
import { hideToast } from "./Toasts";

export class ProjectLinkLoginError extends Error {}

/** Load before mounting either editor: mounting a shell sets its applicationMode. */
export async function loadLinkedProject(projectId: string): Promise<string> {
  await loadLanguage();
  const online = await ServerDataBaseHandler.serverIsOnline();
  universalstorageHandler.temporaryOffline = !online;
  universalstorageHandler.updateStorageHandler();

  const user = state.loggedInUser;
  if (!user) throw new ProjectLinkLoginError("Project link login failed");
  const result = await universalstorageHandler.login(user);
  if (!result) throw new ProjectLinkLoginError("Project link login failed");
  // The local storage handler returns the cached user without issuing a token.
  if (result.token) setSession(result.token);
  state.isLoggedIn = true;
  (window as any).isEstadaLoggedIn = true;

  try {
    const blob = await loadProject(projectId, false);
    if (!blob) throw new Error("Linked project could not be loaded");
    return state.projectMetadata.applicationMode === ApplicationMode.ConceptOne ? "/concept-one" : "/app";
  } finally {
    await hideToast();
  }
}

/** Keep the link in the URL until loading succeeds, without entering project recovery. */
export default function ProjectDeepLink({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = `${location.pathname}${location.search}${location.hash}`;
  const pending = useRef<Promise<string>>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    state._navigate = navigate;
    pending.current ??= loadLinkedProject(projectId);
    void pending.current.then(
      target => { if (active) navigate(target, { replace: true }); },
      error => {
        if (!active) return;
        if (error instanceof ProjectLinkLoginError) {
          setSession(null);
          state.isLoggedIn = false;
          (window as any).isEstadaLoggedIn = false;
          navigate("/login", { replace: true, state: { from: returnPath } });
          return;
        }
        console.error("Project link could not be opened", error);
        setFailed(true);
      },
    );
    return () => { active = false; };
  }, [projectId, navigate, returnPath, attempt]);

  return <main role={failed ? "alert" : "status"} data-testid="project-deep-link" style={{ padding: 24 }}>
    <p>{failed
      ? itx("homePage.projectLoadError", "Beim Laden von Projektelementen ist ein Fehler aufgetreten")
      : itx("homePage.projectLoad", "Projekt wird geladen")}</p>
    {failed && <button type="button" data-testid="project-deep-link-retry" onClick={() => {
      pending.current = undefined;
      setFailed(false);
      setAttempt(value => value + 1);
    }}>{itx("homePage.projectLoadRetry", "Erneut versuchen")}</button>}
  </main>;
}
